// frontend/src/hooks/useSubscriptionNotifications.js
import { useEffect, useCallback, useRef } from 'react';
import { toast } from 'react-toastify';
import { updateAuthToken } from './useAuthToken';
import apiClient from '../api/client';

/**
 * Subscription Change Notification System
 * 
 * Detects JWT token invalidation due to subscription changes and provides
 * user-friendly notifications with automatic refresh capabilities.
 */
const useSubscriptionNotifications = (token) => {
  const isCheckingRef = useRef(false);
  const lastTokenRef = useRef(null);
  const notificationIdRef = useRef(null);

  // Function to check if current token is blacklisted
  const checkTokenValidity = useCallback(async () => {
    // Skip checking if we're on an admin route
    const isAdminRoute = window.location.pathname.startsWith('/admin');
    if (isAdminRoute) {
      return;
    }

    if (!token || isCheckingRef.current) {
      return;
    }

    isCheckingRef.current = true;

    try {
      // Try to fetch user info to detect JWT token invalidation
      const response = await apiClient.get('/auth/me', {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      // If we get here, token is valid
      // Clear any existing notification if token becomes valid again
      if (notificationIdRef.current) {
        toast.dismiss(notificationIdRef.current);
        notificationIdRef.current = null;
      }

    } catch (error) {
      // Check if error indicates token invalidation due to subscription changes
      const isTokenInvalidated = 
        error.response?.status === 401 || 
        error.response?.data?.detail?.includes('token') ||
        error.response?.data?.detail?.includes('subscription') ||
        error.response?.data?.detail?.includes('blacklisted') ||
        error.response?.data?.detail?.includes('invalidated');

      if (isTokenInvalidated && !notificationIdRef.current) {
        showSubscriptionChangeNotification();
      }
    } finally {
      isCheckingRef.current = false;
    }
  }, [token]);

  // Show subscription change notification with refresh action
  const showSubscriptionChangeNotification = useCallback(() => {
    const RefreshButton = ({ closeToast }) => (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <div style={{ fontSize: '14px', fontWeight: '500', color: '#1f2937' }}>
          🔄 Subscription Updated
        </div>
        <div style={{ fontSize: '12px', color: '#6b7280', lineHeight: '1.4' }}>
          Your subscription has changed. Please refresh to access the latest features and settings.
        </div>
        <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
          <button
            onClick={async () => {
              await refreshToken();
              closeToast();
            }}
            style={{
              padding: '6px 12px',
              backgroundColor: '#8b5cf6',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              fontSize: '12px',
              fontWeight: '500',
              cursor: 'pointer'
            }}
          >
            Refresh Now
          </button>
          <button
            onClick={() => {
              window.location.reload();
            }}
            style={{
              padding: '6px 12px',
              backgroundColor: '#059669',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              fontSize: '12px',
              fontWeight: '500',
              cursor: 'pointer'
            }}
          >
            Reload Page
          </button>
          <button
            onClick={closeToast}
            style={{
              padding: '6px 12px',
              backgroundColor: '#6b7280',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              fontSize: '12px',
              fontWeight: '500',
              cursor: 'pointer'
            }}
          >
            Dismiss
          </button>
        </div>
      </div>
    );

    // Show persistent notification
    notificationIdRef.current = toast(RefreshButton, {
      position: "top-right",
      autoClose: false, // Don't auto-close
      hideProgressBar: false,
      closeOnClick: false,
      pauseOnHover: true,
      draggable: false,
      type: 'info',
      style: {
        backgroundColor: '#fef3cd',
        borderLeft: '4px solid #fbbf24',
        padding: '16px'
      }
    });
  }, []);

  // Function to refresh the authentication token
  const refreshToken = useCallback(async () => {
    try {
      const response = await apiClient.post('/auth/refresh-token', {}, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.data?.access_token) {
        // Update token and notify all components
        updateAuthToken(response.data.access_token);
        
        // Show success notification
        toast.success('✅ Session refreshed successfully! Your subscription changes are now active.');
        
        // Clear the subscription change notification
        if (notificationIdRef.current) {
          toast.dismiss(notificationIdRef.current);
          notificationIdRef.current = null;
        }
        
        return true;
      }
    } catch (error) {
      console.warn('Failed to refresh token:', error);
      
      // If refresh fails, suggest manual page reload
      toast.error(
        '⚠️ Unable to refresh automatically. Please reload the page to access your updated subscription.',
        { 
          autoClose: 10000,
          onClick: () => window.location.reload()
        }
      );
      
      return false;
    }
  }, [token]);

  // Check token validity when token changes
  useEffect(() => {
    if (token && token !== lastTokenRef.current) {
      lastTokenRef.current = token;
      
      // Small delay to allow other components to initialize
      const timeoutId = setTimeout(() => {
        checkTokenValidity();
      }, 1000);

      return () => clearTimeout(timeoutId);
    }
  }, [token, checkTokenValidity]);

  // Periodic token validity check (every 30 seconds)
  useEffect(() => {
    if (!token) return;

    const intervalId = setInterval(() => {
      checkTokenValidity();
    }, 30000); // Check every 30 seconds

    return () => clearInterval(intervalId);
  }, [token, checkTokenValidity]);

  // Listen for browser visibility change to check token when tab becomes active
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && token) {
        // Check token when tab becomes active
        setTimeout(checkTokenValidity, 500);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [token, checkTokenValidity]);

  // Listen for subscription-related API errors from other components
  useEffect(() => {
    const handleSubscriptionError = (event) => {
      const { error } = event.detail || {};
      
      if (error?.response?.status === 401 || 
          error?.response?.data?.detail?.includes('subscription') ||
          error?.response?.data?.detail?.includes('token')) {
        
        if (!notificationIdRef.current) {
          showSubscriptionChangeNotification();
        }
      }
    };

    window.addEventListener('subscriptionError', handleSubscriptionError);
    return () => window.removeEventListener('subscriptionError', handleSubscriptionError);
  }, [showSubscriptionChangeNotification]);

  // Cleanup notifications on unmount
  useEffect(() => {
    return () => {
      if (notificationIdRef.current) {
        toast.dismiss(notificationIdRef.current);
      }
    };
  }, []);

  return {
    checkTokenValidity,
    refreshToken,
    isNotificationActive: !!notificationIdRef.current
  };
};

export default useSubscriptionNotifications;

// Utility function for other components to trigger subscription error notifications
export const notifySubscriptionError = (error) => {
  window.dispatchEvent(new CustomEvent('subscriptionError', {
    detail: { error }
  }));
};